import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { api } from '@/lib/api';
import { connectionsApi, type ConnectionView } from '@/lib/connections';
import { mappingsApi, type Mapping } from '@/lib/mappings';
import { summariseSetup, type SetupStep, type StepState } from '@/lib/setup';
import { useAppSelector } from '@/redux/hooks';
import {
  Card,
  LoadingLine,
  Notice,
  PageHeading,
  Refreshing,
  Stat,
  StatusChip,
} from '@/components/ui';
import type { ResolvedScope, ServiceHealth } from '@/lib/types';

/**
 * Screen 3 — Overview.
 *
 * Figures, the five-step setup checklist and the explainer panel. Every figure
 * and every step is derived from what the adapter reports — connections,
 * discovered rooms and mappings — rather than declared here; see
 * `lib/setup.ts`. Steps that are not done say what they are waiting for rather
 * than showing a zero that reads like a broken integration.
 *
 * The four reads are independent on purpose: the service check failing should
 * not blank the checklist, and the checklist failing should not make someone
 * think their sign-in has gone.
 */
export function Overview() {
  const { user, organizations, organizationId } = useAppSelector((s) => s.session);
  const organization = organizations.find((o) => o.id === organizationId) ?? null;

  const [scope, setScope] = useState<ResolvedScope | null>(null);
  const [health, setHealth] = useState<ServiceHealth | null>(null);
  const [connections, setConnections] = useState<ConnectionView[] | null>(null);
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [setupProblem, setSetupProblem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [resolvedScope, serviceHealth, connectionList, mappingList] = await Promise.allSettled([
        api<ResolvedScope>('/auth/scope'),
        api<ServiceHealth>('/health', { anonymous: true, withoutOrganization: true }),
        connectionsApi.list(),
        mappingsApi.list(),
      ]);
      if (cancelled) return;

      if (resolvedScope.status === 'fulfilled') setScope(resolvedScope.value);
      if (serviceHealth.status === 'fulfilled') setHealth(serviceHealth.value);
      if (connectionList.status === 'fulfilled') setConnections(connectionList.value);
      if (mappingList.status === 'fulfilled') setMappings(mappingList.value);

      setProblem(
        resolvedScope.status === 'rejected' || serviceHealth.status === 'rejected'
          ? 'We could not check the service just now. Your sign-in is unaffected.'
          : null,
      );
      // A stale checklist is worse than a missing one: someone acts on it.
      setSetupProblem(
        connectionList.status === 'rejected' || mappingList.status === 'rejected'
          ? 'We could not read your setup just now, so the checklist below may be out of date.'
          : null,
      );
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  /** The checklist reads from two calls; until both land it cannot be trusted. */
  const reading = connections === null || mappings === null;
  const notReady = health?.dependencies.filter((d) => d.state !== 'ok') ?? [];
  const organizationName = scope?.organization_name ?? organization?.name ?? 'This organization';
  const signedInAs =
    [user?.first_name, user?.last_name].filter(Boolean).join(' ') || user?.email || 'you';

  const setup = summariseSetup({
    organizationLabel: `${organizationName}${organization?.domain ? ` · ${organization.domain}` : ''}`,
    connections,
    mappings,
  });

  return (
    <div className="animate-rise flex flex-col gap-[28px] px-[40px] py-[36px] pb-[60px]">
      <PageHeading
        eyebrow="Overview"
        title={organizationName}
        sub={`Signed in as ${signedInAs}${scope?.role ? ` · ${formatRole(scope.role)}` : ''}. ${describeSetup(setup.connection, setup.spacesMapped)}`}
      />

      {problem && <Notice tone="attention" title={problem} />}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-[16px]">
        <Stat
          label="Calendar connected"
          value={setup.connection ? setup.connection.display_name : connections ? 'None' : '—'}
          note={setup.connection?.domain ?? 'Microsoft 365 or Google Workspace'}
          tone={setup.connection?.can_write ? 'good' : 'muted'}
        />
        <Stat
          label="Rooms discovered"
          value={setup.roomsDiscovered ?? '—'}
          note={setup.connection ? `In ${setup.connection.display_name}` : 'Needs a calendar connection'}
          tone={setup.roomsDiscovered ? 'brand' : 'muted'}
        />
        <Stat
          label="Spaces mapped"
          value={setup.spacesMapped ?? '—'}
          note={setup.roomsDiscovered ? 'Linked one to one' : 'Needs discovered rooms'}
          tone={setup.spacesMapped ? 'brand' : 'muted'}
        />
        <Stat
          label="Service"
          value={health === null ? 'Checking' : notReady.length === 0 ? 'Healthy' : 'Degraded'}
          note={
            health === null
              ? 'Asking the adapter'
              : notReady.length === 0
                ? 'Everything it depends on is reachable'
                : `${notReady.length} dependency ${notReady.length === 1 ? 'needs' : 'need'} attention`
          }
          tone={health !== null && notReady.length === 0 ? 'good' : 'muted'}
        />
      </div>

      <div className="grid grid-cols-1 gap-[20px] xl:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
        <Card className="flex flex-col gap-[18px]">
          <div>
            <h2 className="m-0 text-subheading font-semibold">Setup checklist</h2>
            <p className="mt-[4px] mb-0 text-sub text-muted">
              Five steps — then ZenCore takes over booking, displays and sensors.
            </p>
          </div>
          {setupProblem && <Notice tone="attention" title={setupProblem} />}
          {/* Until both reads land, every step derives from `null` and the
              list reads as "nothing done" — which is the one impression a
              working setup must not give while it is simply being read. */}
          {reading && <LoadingLine>Reading your setup…</LoadingLine>}
          <Refreshing busy={reading}>
          <ol className="m-0 flex list-none flex-col p-0">
            {setup.steps.map((step, index) => (
              <Step
                key={step.number}
                number={step.number}
                title={step.title}
                detail={step.detail}
                state={step.state}
                action={step.action}
                last={index === setup.steps.length - 1}
              />
            ))}
          </ol>
          </Refreshing>
        </Card>

        <div className="flex flex-col gap-[16px] rounded-feature bg-brand p-[28px] text-page">
          <p className="zs-eyebrow m-0 text-green-soft">How a booking will flow</p>
          <h2 className="m-0 text-subheading font-semibold">
            The resource calendar is the source of truth.
          </h2>
          <ol className="m-0 flex list-none flex-col gap-[12px] p-0 text-sub text-sky-mist">
            <Flow number={1}>
              Someone books the room in Outlook or Google Calendar — or taps Book Now on the pod
              display.
            </Flow>
            <Flow number={2}>
              The mapping tells ZenCore which space that resource is. Change notifications sync both
              ways; full reconciliation runs on a schedule.
            </Flow>
            <Flow number={3}>
              ZenCore drives the display state, and ZenEdge sensors handle auto-booking and no-show
              release per the space&rsquo;s policy.
            </Flow>
          </ol>
          <p className="mt-auto mb-0 pt-[8px] text-caption text-sky-pale">
            Walk-in pods need no resource — they run on ZenCore&rsquo;s internal store.
          </p>
        </div>
      </div>

      <section className="flex flex-col gap-[14px]">
        <div className="flex items-center gap-[12px]">
          <h2 className="m-0 text-subheading font-semibold">Service status</h2>
          {health !== null && (
            <StatusChip tone={notReady.length === 0 ? 'healthy' : 'attention'}>
              {notReady.length === 0 ? 'All reachable' : 'Needs attention'}
            </StatusChip>
          )}
        </div>
        {health === null ? (
          <LoadingLine>Checking the service…</LoadingLine>
        ) : notReady.length === 0 ? (
          <Notice tone="healthy" title="Everything this tool depends on is reachable." />
        ) : (
          notReady.map((dependency) => (
            <Notice
              key={dependency.name}
              tone={dependency.state === 'down' ? 'broken' : 'attention'}
              title={describeDependency(dependency.name, dependency.state)}
            >
              {dependency.detail}
            </Notice>
          ))
        )}
      </section>
    </div>
  );
}

/**
 * How each step state looks and, more importantly, what it is called.
 *
 * The mark is decorative: the word at the end of the row is what carries the
 * state for anyone not seeing the colour.
 */
const STEP_STATES: Record<StepState, { mark: string; circle: string; label: string; word: string }> =
  {
    done: { mark: '✓', circle: 'bg-green text-ink', label: 'text-green-mid', word: 'Done' },
    in_progress: {
      mark: '⋯',
      circle: 'bg-sky-tint text-brand',
      label: 'text-brand',
      word: 'In progress',
    },
    attention: {
      mark: '!',
      circle: 'bg-warning-soft text-warning',
      label: 'text-warning',
      word: 'Needs attention',
    },
    waiting: { mark: '', circle: 'bg-divider text-muted', label: 'text-muted', word: 'Waiting' },
    checking: { mark: '', circle: 'bg-divider text-muted', label: 'text-muted', word: 'Checking…' },
  };

/** One row of the checklist: a mark, what it is, and where it has got to. */
function Step({
  number,
  title,
  detail,
  state,
  action,
  last = false,
}: {
  number: number;
  title: string;
  detail: string;
  state: StepState;
  action?: SetupStep['action'];
  last?: boolean;
}) {
  const look = STEP_STATES[state];

  return (
    <li className={clsx('flex items-center gap-[14px] py-[14px]', !last && 'border-b border-divider')}>
      <span
        aria-hidden="true"
        className={clsx(
          'flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-full',
          'text-caption font-bold',
          look.circle,
        )}
      >
        {look.mark || number}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-body font-semibold">{title}</span>
        <span className="mt-[2px] block text-caption text-muted">
          {detail}
          {action && (
            <>
              {' '}
              <Link to={action.to} className="font-semibold text-brand underline">
                {action.label}
              </Link>
            </>
          )}
        </span>
      </span>
      <span className={clsx('shrink-0 text-caption font-semibold', look.label)}>{look.word}</span>
    </li>
  );
}

/** One numbered step of the explainer, on the blue panel. */
function Flow({ number, children }: { number: number; children: ReactNode }) {
  return (
    <li className="flex gap-[12px]">
      <span
        aria-hidden="true"
        className="zs-tagline w-[24px] shrink-0 text-heading font-semibold text-green-soft"
      >
        {number}
      </span>
      <span>{children}</span>
    </li>
  );
}

/** The second half of the page subtitle: where this organisation has got to. */
function describeSetup(connection: ConnectionView | null, spacesMapped: number | null): string {
  if (!connection) {
    return 'Nothing is connected yet — connecting a calendar is the first step.';
  }
  if (!spacesMapped) {
    return `${connection.display_name} is connected. Mapping your spaces is what makes bookings flow.`;
  }
  return `${connection.display_name} is connected, with ${spacesMapped} ${spacesMapped === 1 ? 'space' : 'spaces'} mapped.`;
}

/**
 * Dependency names are for operators; these headlines are for everyone else.
 *
 * Each one says what is not happening, not which component is unwell. "Redis is
 * down" is unactionable for a facilities manager; "bookings are not syncing" is
 * the thing they actually need to know, and it is true whichever way Redis
 * failed.
 */
const DEPENDENCY_HEADLINES: Record<string, { down: string; degraded: string }> = {
  zencore: {
    down: 'We cannot reach ZenSpace',
    degraded: 'ZenSpace is responding slowly',
  },
  database: {
    down: 'We cannot reach our own records',
    degraded: 'Our records are not fully available',
  },
  redis: {
    // Deliberately about the consequence. Nothing is lost while this is down —
    // work queues up and resumes — so the wording should not read as data loss.
    down: 'Bookings are not being kept in step right now',
    degraded: 'Background sync is running behind',
  },
};

function describeDependency(name: string, state: string): string {
  const headline = DEPENDENCY_HEADLINES[name];
  if (headline) {
    return state === 'down' ? headline.down : headline.degraded;
  }
  // An unknown dependency should still read as a sentence rather than a key.
  return state === 'down' ? 'Something we depend on is unavailable' : 'Something is not fully ready';
}

/** `org_admin` reads as a database value; "an administrator" reads as English. */
function formatRole(role: string): string {
  const spelled = role.replace(/[_-]+/g, ' ').trim().toLowerCase();
  if (spelled.includes('admin')) return 'an administrator';
  if (spelled.includes('manager')) return 'a manager';
  if (spelled.includes('member')) return 'a member';
  return `a ${spelled}`;
}
